import React, { ReactElement, useState } from 'react'
import {
    BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'
import { CustomTooltipProps, GRAPH_COLOURS, GRAPH_MARGIN, GraphProps } from './Graph.d'

const CustomTooltip = ({ active, payload, label, extraFields, xLabelFormatter }: CustomTooltipProps): ReactElement | null => {
    if (!active || !payload?.length) return null
    const row = payload[0]?.payload
    const formattedLabel = label && xLabelFormatter ? xLabelFormatter(label) : label
    return <div className="graph__tooltip">
        <p className="graph__tooltip-label">{ formattedLabel }</p>
        { payload.map(item => <p
            key={ item.name }
            className="graph__tooltip-item"
            style={ { color: item.color } }>{ item.name }: { item.value.toLocaleString() }
        </p>) }
        { extraFields.map(field => row?.[field] != null && <p
            key={ field }
            className="graph__tooltip-extra">{ fieldLabel(field) }: { (row[field] as number).toPrecision(3) }
        </p>) }
    </div>
}

const fieldLabel = (field: string) => field
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase())

type YAxisLabelProps = {
    viewBox?: { x: number, y: number, width: number, height: number }
    value?: string
    colour: string
    active: boolean
    side: 'left' | 'right'
    onAxisClick: () => void
}

const YAxisLabel = ({ viewBox, value = '', colour, active, side, onAxisClick }: YAxisLabelProps): ReactElement | null => {
    if (!viewBox) return null
    const { x, y, width, height } = viewBox
    const cx = side === 'left' ? x + 14 : x + width - 14
    const cy = y + height / 2
    return <text
        x={ cx }
        y={ cy }
        transform={ `rotate(-90, ${cx}, ${cy})` }
        textAnchor="middle"
        fill={ colour }
        stroke={ colour }
        strokeWidth={ active ? 0.7 : 0 }
        className="graph__y-axis-label"
        onClick={ (e) => {
            e.stopPropagation()
            onAxisClick()
        } }>
        { value }
    </text>
}

const GraphBar = ({ data, onClick }: GraphProps): ReactElement => {
    const { xAxis = 'date', yAxes = [], title, yTitle, fieldNames = {}, xLabelFormatter, xTickFormatter, data: rows } = data
    const label = (field: string) => fieldNames[field] ?? fieldLabel(field)
    const dual = yAxes.length === 2
    const [sortField, setSortField] = useState(yAxes[0] ?? '')
    const sortedRows = sortField === yAxes[0]
        ? rows
        : [...rows].sort((a, b) => ((b[sortField] as number) ?? -Infinity) - ((a[sortField] as number) ?? -Infinity))
    const extraFields = rows.length > 0
        ? Object.keys(rows[0]).filter(k => k !== xAxis && !yAxes.includes(k) && typeof rows[0][k] === 'number')
        : []
    return <div className="graph" onClick={ onClick }>
        { title && <h3 className="graph__title">{ title }</h3> }
        <div className="graph__chart">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ sortedRows as Record<string, unknown>[] } margin={ GRAPH_MARGIN }>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={ xAxis } tickFormatter={ xTickFormatter } />
                    <YAxis
                        yAxisId="left"
                        width={ 70 }
                        tick={ { fill: GRAPH_COLOURS[0] } }
                        label={ <YAxisLabel
                            value={ label(dual ? yAxes[0] : (yTitle ?? yAxes[0] ?? '')) }
                            colour={ GRAPH_COLOURS[0] }
                            active={ sortField === yAxes[0] }
                            side="left"
                            onAxisClick={ () => setSortField(yAxes[0]) } /> } />
                    { dual && <YAxis
                        yAxisId="right"
                        orientation="right"
                        width={ 70 }
                        tick={ { fill: GRAPH_COLOURS[1] } }
                        label={ <YAxisLabel
                            value={ label(yAxes[1]) }
                            colour={ GRAPH_COLOURS[1] }
                            active={ sortField === yAxes[1] }
                            side="right"
                            onAxisClick={ () => setSortField(yAxes[1]) } /> } /> }
                    <Tooltip content={ <CustomTooltip extraFields={ extraFields } xLabelFormatter={ xLabelFormatter } /> } />
                    { yAxes.map((field, i) => (
                        <Bar
                            key={ field }
                            dataKey={ field }
                            name={ label(field) }
                            fill={ GRAPH_COLOURS[i % GRAPH_COLOURS.length] }
                            yAxisId={ dual ? (i === 0 ? 'left' : 'right') : 'left' } />
                    )) }
                </BarChart>
            </ResponsiveContainer>
        </div>
    </div>
}

export default GraphBar
